/**
 * 스포츠 베팅 API 서비스
 * 외부 배당 API 연동 — API 엔드포인트를 통해 실시간 데이터 제공
 * 종목 목록은 API(/sports/types)에서 동적으로 가져옴 (하드코딩 없음)
 */
import api from './api';

// 베팅 탭 정의 (DB member_level_sports_settings의 bet_type과 일치)
// '전체' 탭: betType 파라미터를 전송하지 않아 서버에서 필터 미적용 (전체 경기 반환)
const BET_TABS = [
  { id: 'prematch_all', name: '전체', category: 'prematch' },
  { id: 'prematch_domestic', name: '국내', category: 'prematch' },
  { id: 'prematch_overseas', name: '해외', category: 'prematch' },
  { id: 'prematch_special', name: '스페셜', category: 'prematch' },
  { id: 'live', name: '라이브', category: 'live' },
];

/**
 * 상단 상위 탭 — 마켓 유형별 구분.
 * - combo: 모든 프리매치 마켓 (기존 메인 1X2 + "+N" 드롭다운 유지)
 * - winner: 승무패(1X2) 마켓만, 드롭다운 숨김
 * - handicap: 핸디캡 마켓만, 드롭다운 숨김 (경기별 상세 API 로 핸디캡 배당 조회)
 * - overunder: 언오버(오버/언더) 마켓만, 드롭다운 숨김 (상세 API 로 오버/언더 배당 조회)
 * 스페셜 / 라이브는 activeTab('prematch_special' / 'live') 이 상위 탭 역할을 겸한다.
 */
const MARKET_TABS = [
  { id: 'combo', name: '조합배팅' },
  { id: 'winner', name: '승무패' },
  { id: 'handicap', name: '핸디캡' },
  { id: 'overunder', name: '언오버' },
];

/**
 * 프론트엔드 selection 객체를 백엔드 placeBet 요청 구조로 변환
 *
 * [2026-04-24 SPEC S9] 수동 경기 matchId/marketId 포맷 대응:
 *   자동 경기: matchId='fixture-{externalId}',  marketId='mk-{fixtureId}-{marketKey}'
 *   수동 경기: matchId='fixture-manual-{localPk}', marketId='mk-manual-{localPk}-{marketId}[-{group}]'
 *   'manual-' 접두사를 인식해 fixture_id(int) / market_id(int) 를 올바르게 추출한다.
 *   백엔드 user-api/routes/sportsBetting.js 는 로컬 PK 우선 조회 → external_id fallback 로직이
 *   이미 준비되어 있으므로, 프론트가 수동 경기의 로컬 PK 를 fixture_id 로 전달하면 베팅 저장이
 *   정상 수행된다.
 */
function convertSelectionForBackend(sel) {
  const isManual = typeof sel.matchId === 'string' && sel.matchId.startsWith('fixture-manual-');

  const fixtureId = isManual
    ? (parseInt(sel.matchId.replace('fixture-manual-', ''), 10) || null)
    : (sel.matchId?.startsWith('fixture-')
        ? parseInt(sel.matchId.split('-').slice(1).join('-'), 10)
        : parseInt(sel.matchId, 10) || null);

  // marketId 파싱:
  //   자동: 'mk-{fixtureId}-{marketKey}[|{line}]'  → marketKey 반환
  //   수동: 'mk-manual-{localPk}-{marketId}[-{group}]' → marketId 반환 (odds_group 은 선택)
  //   두 경우 모두 '|' 이후 line 은 잘라냄.
  let marketId = null;
  if (sel.marketId) {
    const cleaned = sel.marketId.split('|')[0];
    const parts = cleaned.split('-');
    if (parts[0] === 'mk' && parts[1] === 'manual') {
      // mk-manual-{localPk}-{marketId}[-{group}] — 세 번째 토큰(index 3)이 market_id
      marketId = parts[3] || null;
    } else {
      // mk-{fixtureId}-{marketKey} — index 2 이후 전체
      marketId = parts.length >= 3 ? parts.slice(2).join('-') : cleaned;
    }
  }

  // selection 우선순위:
  //   1) selectionKey ('home'/'draw'/'away') — 1X2 마켓은 명시적 키 사용 (핵심 버그 방지)
  //   2) outcomeId.split('-').pop() — 핸디캡/오버언더/스코어 등 기존 매칭 방식 유지
  //      (핸디캡 selection에 dash가 포함되면 손실되는 한계 있음 — 백엔드 transformMarkets 수정 필요)
  const isOneXTwo = sel.selectionKey === 'home' || sel.selectionKey === 'draw' || sel.selectionKey === 'away';
  const selectionName = isOneXTwo
    ? sel.selectionKey
    : (sel.outcomeId ? sel.outcomeId.split('-').pop() : (sel.outcomeName || null));

  let homeTeam = null;
  let awayTeam = null;
  if (sel.matchInfo && sel.matchInfo.includes(' vs ')) {
    const parts = sel.matchInfo.split(' vs ');
    homeTeam = parts[0]?.trim() || null;
    awayTeam = parts[1]?.trim() || null;
  }

  return {
    fixture_id: fixtureId,
    market_id: marketId,
    // [2026-05-04] market_db_id — 동일경기 조합 차단 서버 가드용 sports_markets.id 정수.
    //   user-api 응답에서 받아 selection 에 보존된 marketDbId 를 그대로 전송.
    //   서버는 이 값을 1순위로 신뢰하고 미전송/0/null 이면 fallback lookup 사용.
    market_db_id: sel.marketDbId || null,
    selection: selectionName,
    // WHY: addSelection 에서 보존한 nmsports 원본 line 값을 백엔드 INSERT 까지
    //   전달. 라인 없는 마켓(승무패 등)은 null. 백엔드 sports_bet_details.bet_line
    //   컬럼 매핑 — 자동 정산·셀별 베팅금 분리 표시의 단일 식별자.
    bet_line: (typeof sel.line === 'number' && Number.isFinite(sel.line)) ? sel.line : null,
    odds: sel.odds,
    fixture_home_team: homeTeam,
    fixture_away_team: awayTeam,
    fixture_start_time: sel.startTime || null,
    sport_type: sel.sportType || null,
    sport_type_id: sel.sportTypeId || null,
    league_id: sel.leagueId || null,
    country_name: sel.countryName || null,
    league_name: sel.leagueName || null,
    match_status: sel.matchStatus || null,
    market_name: sel.marketName || null,
    league_gs_id: sel.leagueGsId || null,
    home_team_gs_id: sel.homeTeamId || null,
    away_team_gs_id: sel.awayTeamId || null,
    odds_home: sel.oddsHome || null,
    odds_draw: sel.oddsDraw || null,
    odds_away: sel.oddsAway || null,
  };
}

// ── 종목 데이터 캐시 (API에서 동적 로드) ──
let cachedSportTypes = null;
// 변경 구독자 — 컴포넌트가 종목 로드 완료를 감지할 수 있음
let sportTypesListeners = [];
// 진행 중인 fetch promise — 동시 호출이 모두 동일 promise 를 공유해 중복 네트워크
// 요청을 방지한다 (Phase 3, 2026-04-09). React StrictMode 이중 마운트 + 모듈 로드시
// initSportTypes + 컴포넌트 마운트 시 fetchSportTypes 등으로 과거 측정 기준 /sports/types
// 가 초기 로드 시 3회까지 병렬 호출되던 문제를 여기서 coalesce한다.
let pendingSportTypesFetch = null;

function notifySportTypesListeners() {
  sportTypesListeners.forEach(fn => fn(cachedSportTypes));
}

/**
 * /sports/types 단일 요청 + 인플라이트 코얼레싱 헬퍼
 *
 * - 캐시 히트 시 즉시 반환(네트워크 0회)
 * - 이미 진행 중인 요청이 있으면 그 promise 를 재사용(네트워크 0회)
 * - 아니면 네트워크 요청 1회 후 캐시 채움
 *
 * initSportTypes 자동 호출과 여러 컴포넌트의 fetchSportTypes 호출이 동일 promise 를
 * 공유하도록 만들어 중복 요청을 제거한다.
 */
function requestSportTypes() {
  if (cachedSportTypes) return Promise.resolve(cachedSportTypes);
  if (pendingSportTypesFetch) return pendingSportTypesFetch;

  pendingSportTypesFetch = (async () => {
    try {
      const response = await api.get('/sports/types');
      if (response.data.success && Array.isArray(response.data.data) && response.data.data.length > 0) {
        // [2026-10-19 W93] 배당 서버 장애 중 응답(손등록 경기 종목만 들어 있음)은 세션 내내 담아 두지 않는다 — 회복 뒤 자동 종목 탭이
        //   새로고침 전까지 안 보이던 문제(독립 검토 지적 11). 이번 화면에는 받은 목록을 그대로 쓴다.
        if (response.data.oddsFeedUnavailable) return response.data.data;
        cachedSportTypes = response.data.data;
        notifySportTypesListeners();
        return cachedSportTypes;
      }
      return [];
    } catch (error) {
      console.warn('[스포츠] /sports/types 조회 실패:', error.message);
      return [];
    } finally {
      // 다음 요청부터는 cachedSportTypes 히트 또는 재시도 가능하도록 초기화
      pendingSportTypesFetch = null;
    }
  })();

  return pendingSportTypesFetch;
}

// 앱 로드 시 종목 데이터를 비동기로 가져와 캐시
// (이후 컴포넌트 마운트 시 fetchSportTypes 호출이 있더라도 pendingSportTypesFetch
//  코얼레싱에 의해 중복 요청은 발생하지 않는다.)
// [2026-10-31] 로그인 토큰이 있을 때만 미리 받는다. 종목 목록 창구는 로그인 회원 전용이라, 비로그인 방문자는 모든 화면을 처음 열 때마다
//   401 실패 요청을 남겼다(2026-10-31 ur3 실측). 종전에도 그 실패로 담긴 값이 없었으므로 이 조건으로 바뀌는 화면 상태는 없다 — 로그인
//   회원은 종전처럼 미리 받고, 로그인하지 않은 채 연 뒤 로그인하면 종목을 쓰는 화면이 열릴 때 fetchSportTypes 로 받는다(종전과 같음).
//   토큰 자리는 services/api.js 요청 인터셉터와 같다. 저장소를 읽을 수 없는 브라우저(저장소 차단)에서 모듈 불러오기가 깨지지 않도록
//   읽기 실패는 '토큰 없음'으로 본다.
const hasLoginToken = (() => { try { return !!localStorage.getItem('token'); } catch (e) { return false; } })();
if (hasLoginToken) requestSportTypes();

const sportsService = {
  /**
   * 스포츠 종목 목록 반환 (동기)
   * API에서 사전 로드된 캐시 데이터 반환, 아직 로드 안됐으면 빈 배열
   */
  getSportTypes() {
    return cachedSportTypes || [];
  },

  /**
   * 스포츠 종목 목록 비동기 조회 (캐시 갱신 포함)
   * 컴포넌트 마운트 시 확실히 가져오기 위해 사용.
   * Phase 3 (2026-04-09): requestSportTypes 로 위임하여 동시 호출 코얼레싱을 보장.
   */
  async fetchSportTypes() {
    return requestSportTypes();
  },

  /**
   * 종목 데이터 변경 구독
   * @returns {function} unsubscribe 함수
   */
  onSportTypesChange(listener) {
    sportTypesListeners.push(listener);
    // 이미 로드됐으면 즉시 호출
    if (cachedSportTypes) listener(cachedSportTypes);
    return () => {
      sportTypesListeners = sportTypesListeners.filter(fn => fn !== listener);
    };
  },

  /** 베팅 탭 목록 반환 (정적 상수) */
  getBetTabs() {
    return BET_TABS;
  },

  /**
   * 경기 목록 조회
   * betType이 'live'이면 /sports/inplay 엔드포인트를 호출
   * 그 외에는 /sports/matches 엔드포인트를 호출
   * API 오류 시 빈 배열 반환
   */
  async getMatches(params = {}, { throwOnError = false, withMeta = false } = {}) {
    // [2026-10-19 W93] withMeta — { data, oddsFeedUnavailable } 로 돌려준다(손등록 경기 배당 변경 뒤 '조용히 다시 받기' 가 배당 서버
    //   장애 응답으로 자동 경기 목록을 지우지 않게, 독립 검토 지적 12). 기본값(false)은 종전과 같다(목록 배열).
    // [2026-10-18 W93] throwOnError — 손등록 경기 배당 변경 알림 뒤 '조용히 다시 받기'용. 실패를 알려야 화면 목록을 빈 목록으로 덮지 않는다.
    //   기본값(false)은 종전과 같다(실패하면 빈 배열).
    try {
      let response;

      if (params.betType === 'live') {
        // 라이브 경기는 전용 인플레이 엔드포인트 사용
        const inplayParams = { ...params };
        delete inplayParams.betType; // inplay 엔드포인트에서는 betType 불필요
        response = await api.get('/sports/inplay', { params: inplayParams });
      } else {
        // '전체' 탭(prematch_all)이면 betType 파라미터를 제거하여 서버에서 필터 미적용
        const matchParams = { ...params };
        if (matchParams.betType === 'prematch_all') {
          delete matchParams.betType;
        }
        response = await api.get('/sports/matches', { params: matchParams });
      }

      if (response.data.success) {
        if (withMeta) return { data: response.data.data, oddsFeedUnavailable: !!response.data.oddsFeedUnavailable };
        return response.data.data;
      }
      if (throwOnError) throw new Error('getMatches: success=false');
      return [];
    } catch (error) {
      console.warn('[스포츠] getMatches API 오류:', error.message);
      if (throwOnError) throw error;
      return [];
    }
  },

  /** 경기 상세 조회 (마켓/배당 포함)
   *  [2026-04-24 SPEC S15c] specialOnly 옵션 — 스페셜 탭에서 펼칠 때 전달.
   *    서버가 A(feed_type='special') OR B(is_special=1) OR C(is_featured=1, 경기 단위 스페)
   *    규칙으로 마켓 필터 적용. 다른 탭에선 미전달 → 기존 동작 유지.
   */
  async getMatchDetail(matchId, { specialOnly = false } = {}) {
    try {
      const params = specialOnly ? { specialOnly: 1 } : undefined;
      const response = await api.get(`/sports/matches/${matchId}`, params ? { params } : undefined);
      if (response.data.success) {
        return response.data.data;
      }
      return null;
    } catch (error) {
      console.warn('[스포츠] getMatchDetail API 오류:', error.message);
      return null;
    }
  },

  /** 리그 목록 조회 */
  async getLeagues(sportType) {
    try {
      const response = await api.get('/sports/leagues', { params: { sportType } });
      if (response.data.success) {
        return response.data.data;
      }
      return [];
    } catch (error) {
      console.warn('[스포츠] getLeagues API 오류:', error.message);
      return [];
    }
  },

  /**
   * 베팅 요청
   * @param {Object} betData - { betAmount, selections, betType, totalOdds }
   */
  async placeBet(betData) {
    const convertedSelections = (betData.selections || []).map(convertSelectionForBackend);

    // selection의 betType(prematch_domestic/prematch_overseas/live/prematch_special)에서
    // DB enum(prematch/live/special) 도출 — 멀티 베팅 시 하나라도 live면 live
    let sportsCategory = 'prematch';
    const selBetTypes = (betData.selections || []).map(s => s.betType).filter(Boolean);
    if (selBetTypes.some(t => t === 'live')) {
      sportsCategory = 'live';
    } else if (selBetTypes.some(t => t === 'prematch_special')) {
      sportsCategory = 'special';
    }

    const payload = {
      betAmount: betData.betAmount,
      selections: convertedSelections,
      betType: betData.betType,
      totalOdds: betData.totalOdds,
      sportsCategory,
      applyBonus: betData.applyBonus,
    };

    const response = await api.post('/sports-betting/place-bet', payload);
    return response.data;
  },

  /** 베팅 내역 조회 */
  async getBetHistory(params = {}) {
    const response = await api.get('/sports/bets/history', { params });
    return response.data;
  },

  /**
   * 경기결과 목록 조회 — 경기결과 페이지 전용
   *
   * WHY: /matches, /inplay 는 "진행 가능한" 경기만 반환한다.
   *       경기결과는 종료/정산된 경기가 대상이므로 별도 엔드포인트를 쓴다.
   *
   * @param {Object} params
   *   - sport_key    종목 필터 (soccer/basket/...) — 서버 필터
   *   - date_from    ISO datetime — 서버 필터 (기본: 오늘)
   *   - date_to      ISO datetime — 서버 필터
   *   - page, limit  페이지네이션
   *   - (※ 탭 필터는 프론트에서 markets 키 기반으로 수행 — 서버는 전체 markets 반환)
   * @returns { success, data, pagination }
   */
  async getResults(params = {}) {
    try {
      const response = await api.get('/sports/results', { params });
      return response.data;
    } catch (error) {
      console.warn('[스포츠] 경기결과 조회 실패:', error.message);
      return { success: false, data: [], pagination: null };
    }
  },

  /** 베팅 취소 */
  async cancelBet(betId) {
    const response = await api.post('/sports-betting/cancel-bet', { betId });
    return response.data;
  },

  /** 취소 설정 조회 (경기 시작 N분 전부터 취소 불가) */
  async getCancelConfig() {
    const response = await api.get('/sports-betting/cancel-config');
    return response.data;
  },

  /**
   * 보너스 배당 설정 조회
   * 기능 활성화 시 is_active=1인 폴더별 보너스 배수 목록 반환
   * 응답: { enabled, folders: [{ folder_count, bonus_multiplier }, ...] }
   */
  async getBonusConfig() {
    try {
      const response = await api.get('/sports-betting/bonus-config');
      return response.data;
    } catch (error) {
      console.warn('[스포츠] 보너스 설정 조회 실패:', error.message);
      return { success: true, data: { enabled: false, mode: 'manual', folders: [] } };
    }
  },

  /**
   * 베팅슬립 싱글/멀티 탭 사용 설정 조회 (관리자 설정)
   * 응답: { enabled_single, enabled_multi }
   * 조회 실패 시 기본값(둘 다 true) 반환 — 기존 유저 경험 유지
   */
  async getSlipTabsConfig() {
    try {
      const response = await api.get('/sports-betting/slip-tabs-config');
      return response.data;
    } catch (error) {
      console.warn('[스포츠] 베팅슬립 탭 설정 조회 실패:', error.message);
      return { success: true, data: { enabled_single: true, enabled_multi: true } };
    }
  },

  /**
   * 베팅카트 레이아웃 조회 (관리자 설정)
   * 응답: { layout: 'right_panel' | 'bottom_bar' }
   * WHY: ur5 에서 우측/하단 카트 중 하나만 렌더. 조회 실패 시 'right_panel' 기본값 반환.
   */
  async getCartLayout() {
    try {
      const response = await api.get('/sports-betting/cart-layout');
      return response.data;
    } catch (error) {
      console.warn('[스포츠] 카트 레이아웃 조회 실패:', error.message);
      return { success: true, data: { layout: 'right_panel' } };
    }
  },

  // [2026-05-03] 동일경기 폴더 차단 페어 조회 (관리자 운영 룰)
  // 응답: [{ market_id_1, market_id_2, market1_name, market2_name, ... }]
  // 실패 시 빈 배열 — 클라 가드는 비활성, 서버 가드가 최종 방어 유지(역회귀 방지)
  async getBlockedCombinations() {
    try {
      const response = await api.get('/sports-betting/blocked-combinations');
      return response.data;
    } catch (error) {
      console.warn('[스포츠] 차단 페어 조회 실패:', error.message);
      return { success: true, data: [] };
    }
  },
};

// [2026-10-18 W93] 손등록 경기 배당·마켓 변경 알림(sportsManualOdds)을 받은 뒤 다시 받기까지 기다리는 시간(밀리초).
//   운영자가 여러 마켓을 잇달아 고쳐도 한 번만 받게 한다(목록·펼친 마켓·베팅슬립이 같은 값을 쓴다).
const MANUAL_ODDS_REFRESH_DELAY_MS = 800;

export default sportsService;
export { BET_TABS, MARKET_TABS, MANUAL_ODDS_REFRESH_DELAY_MS };
